import { Button } from "primereact/button";
import { Password } from "primereact/password";
import {
  PASSWORD_MIN_LENGTH,
  evaluatePasswordStrength,
  generatePassword,
} from "./opcUaUtils";

interface PasswordFieldsProps {
  password: string;
  confirmPassword: string;
  setPassword: (password: string) => void;
  setConfirmPassword: (confirmPassword: string) => void;
  disabled?: boolean;
}

/**
 * Why the pair cannot be saved, or null when it can. A message rather than a boolean so the
 * caller reports the actual problem — "too short" and "does not match" are not interchangeable.
 */
export const validatePasswordPair = (
  password: string,
  confirmPassword: string
): string | null => {
  if (!password) return "Enter a password and confirm it.";
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (password !== confirmPassword) return "Passwords do not match.";
  return null;
};

const PasswordFields = ({
  password,
  confirmPassword,
  setPassword,
  setConfirmPassword,
  disabled,
}: PasswordFieldsProps) => {
  const strength = evaluatePasswordStrength(password);
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleGenerate = () => {
    // Fill both fields so the operator can copy the value before saving; it is not
    // retrievable from the server afterwards.
    const generated = generatePassword();
    setPassword(generated);
    setConfirmPassword(generated);
  };

  return (
    <>
      <div className="p-field flex flex-column gap-2">
        <div className="flex justify-content-between align-items-center gap-3">
          <label htmlFor="opcUaPassword" className="white-space-nowrap">
            Password *
          </label>
          {/* w-auto overrides `.p-fluid .p-button { width: 100% }`, which would otherwise
              take the whole row and squeeze the label onto two lines. */}
          <Button
            label="Generate"
            icon="pi pi-refresh"
            className="p-button-text p-button-sm w-auto flex-shrink-0"
            type="button"
            onClick={handleGenerate}
            disabled={disabled}
          />
        </div>
        <Password
          inputId="opcUaPassword"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          toggleMask
          feedback={false}
          disabled={disabled}
          autoComplete="new-password"
          className="w-full"
          inputClassName="w-full"
        />
        {tooShort && (
          <small className="p-error">
            At least {PASSWORD_MIN_LENGTH} characters.
          </small>
        )}
        {password.length > 0 && (
          <div className="flex align-items-center gap-2">
            <div
              style={{
                flex: 1,
                height: "6px",
                borderRadius: "3px",
                background: "var(--surface-border)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${strength.score}%`,
                  height: "100%",
                  background: strength.color,
                  transition: "width 120ms linear",
                }}
              />
            </div>
            <small style={{ color: strength.color }}>{strength.label}</small>
          </div>
        )}
      </div>

      <div className="p-field flex flex-column gap-2">
        <label htmlFor="opcUaConfirmPassword">Confirm password *</label>
        <Password
          inputId="opcUaConfirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          toggleMask
          feedback={false}
          disabled={disabled}
          autoComplete="new-password"
          className="w-full"
          inputClassName="w-full"
        />
        {mismatch && <small className="p-error">Passwords do not match.</small>}
      </div>
    </>
  );
};

export default PasswordFields;
